import { NotificationList } from 'components/common/NotificationList'
import { put, takeLatest } from 'redux-saga/effects'
import {
  makeGetRequest,
  makePutRequest,
  makeDeleteRequest,
  makePostRequest,
  makePatchRequest
} from 'utils/api'
import { formatResponse, formatErrorMessage } from 'utils/formatting'

import { toDeal, augumentDealObject } from './serializers'
import { fromBoardItems, toTask, fromCompanies } from 'utils/serializers'

import * as ACT from './actions'
import { PUSH_NOTIFICATION } from 'actions/actionTypes'
import { refreshNotifications, utcDate } from 'utils/helpers'
import cloneDeep from 'lodash/cloneDeep'
import { EMAIL_SPLIT_MODE } from 'utils/constants'

function* fetchDeal(action) {
  try {
    const { id } = action.payload
    const url = `/v1/deals/${id}`
    const deal = yield makeGetRequest(url)

    const companiesWithType = yield makeGetRequest(`/v1/deals/deal-company-links/${id}`)

    yield put({
      type: ACT.DEAL_FETCH_SUCCESS,
      payload: {
        deal: augumentDealObject(cloneDeep(deal), { companiesWithType })
      }
    })
  } catch (error) {
    const { navigate } = action.payload
    if (error.response.status === 403) {
      navigate('/opportunities')
    }
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue fetching the Opportunity',
        message: (
          <NotificationList
            lines={formatResponse(
              error.response.status === 403
                ? "You currently don't have permission to see this information"
                : formatErrorMessage(error)
            )}
          />
        ),
        level: 'error'
      }
    })
  }
}

function* deleteDeal(action) {
  try {
    const { id, navigate } = action.payload
    const url = `/v1/deals/${id}`
    yield makeDeleteRequest(url)

    yield put({
      type: ACT.DEAL_DELETE_SUCCESS
    })
    navigate('/opportunities')
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue deleting the Opportunity',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* updateDealDate(action) {
  const { date, id, followUpView } = action.payload

  try {
    const url = `/v1/deals/${id}`
    const obj = followUpView ? { followUpDate: utcDate(date) } : { dueDate: utcDate(date) }
    const response = yield makePutRequest(url, obj)

    yield put({
      type: ACT.DEAL_FETCH_SUCCESS,
      payload: {
        deal: augumentDealObject(cloneDeep(response))
      }
    })

    yield put({
      type: ACT.DEAL_DATE_UPDATE_SUCCESS,
      payload: {
        deal: response
      }
    })

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Opportunity updated successfully',
        level: 'success'
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue updating Opportunity',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* updateDeal(action) {
  const { deal, fromDealDetails, callback } = action.payload

  try {
    const url = `/v1/deals/${deal.id}`
    let response = yield makePutRequest(url, toDeal(deal))

    let augumentedDeal = augumentDealObject(cloneDeep(response))
    // add clients property from the old deal object so the client's contact list is not lost
    if (fromDealDetails) augumentedDeal.clients = deal.clients

    yield put({
      type: ACT.DEAL_FETCH_SUCCESS,
      payload: {
        deal: augumentedDeal
      }
    })

    yield put({
      type: ACT.DEAL_UPDATE_SUCCESS
    })

    yield put({
      type: ACT.DEAL_SUMMARY_INFO_FETCH_REQUESTED,
      payload: {
        id: augumentedDeal.id
      }
    })

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Opportunity updated successfully',
        level: 'success'
      }
    })

    if (callback) {
      yield put(callback())
    }
  } catch (error) {
    const message = formatResponse(formatErrorMessage(error))
    if (
      message ===
      'org.springframework.web.util.NestedServletException: Request processing failed; nested exception is com.ideal.crm.contacts.exceptions.CustomException: Deal is still slipping ! Please review !'
    ) {
      yield put({
        type: ACT.DEAL_HEALTH_ERROR,
        payload: {
          healthError: true
        }
      })
      yield put({
        type: ACT.DEAL_UPDATE_SUCCESS
      })
    } else {
      yield put({
        type: PUSH_NOTIFICATION,
        payload: {
          title: 'There was an issue updating Opportunity',
          message: <NotificationList lines={message} />,
          level: 'error'
        }
      })
    }
  }
}

function* updateDealContractorAwarded(action) {
  const { deal, status, oldStatus, userId, filters } = action.payload

  const {
    startDate,
    endDate,
    categories,
    contractTypes,
    awarded,
    geographicalAreas,
    priorities,
    scopes,
    sizes,
    sources,
    workTypes,
    teams
  } = filters

  try {
    let url = `/v1/deals/${deal.id}`
    let response = yield makePatchRequest(url, {
      awardedCompanyId: deal.awardedCompany ? deal.awardedCompany.value : null,
      bidStatus: deal.bidStatus?.value,
      followUpDate: deal.followUpDate,
      noWinReason: deal.noWinReason ? deal.noWinReason.code : null
    })
    const cloneDeal = augumentDealObject(cloneDeep(response))

    yield put({
      type: ACT.DEAL_FETCH_SUCCESS,
      payload: {
        deal: cloneDeal
      }
    })

    yield put({
      type: ACT.DEAL_FOLLOWUP_DATE_UPDATE_SUCCESS,
      payload: {
        deal: cloneDeal,
        oldStatus,
        awardedCompany: cloneDeal.awardedCompany
      }
    })

    url = `/v1/deals-board/${deal.id}/changestatus/check`
    response = yield makePutRequest(url, {
      newStatusCode: status
    })

    yield put({
      type: ACT.DEAL_UPDATE_STATUS_SUCCESS,
      payload: {
        id: deal.id,
        status,
        oldStatus,
        willMove: response,
        bidStatus: deal.bidStatus?.value
      }
    })

    yield put({
      type: ACT.DEAL_BOARD_CONTRACTOR_AWARDED_UPDATE_SUCCESS
    })

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Opportunity updated successfully',
        level: 'success'
      }
    })
    yield put({
      type: ACT.DEALS_BOARD_FETCH_DEALS_HEADER,
      payload: {
        id: userId,
        startDate,
        endDate,
        categories,
        contractTypes,
        awarded,
        geographicalAreas,
        priorities,
        scopes,
        sizes,
        sources,
        workTypes,
        teams
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue updating Opportunity',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* updateDealStatus(action) {
  const {
    id,
    status,
    oldStatus,
    dealFollowupDate,
    proposalsFollowupDate,
    userId,
    filters,
    awardedCompanyId
  } = action.payload
  try {
    const {
      endDate,
      startDate,
      categories,
      contractTypes,
      awarded,
      geographicalAreas,
      priorities,
      scopes,
      sizes,
      sources,
      workTypes,
      teams
    } = filters
    const url = `/v1/deals-board/${id}/changestatus/check`
    const response = yield makePutRequest(url, {
      newStatusCode: status
    })

    yield put({
      type: ACT.DEAL_UPDATE_STATUS_SUCCESS,
      payload: {
        id,
        status,
        oldStatus,
        willMove: response.statusChanged,
        wonOn: response.wonOn,
        lostOn: response.lostOn
      }
    })

    if (dealFollowupDate) {
      yield put({
        type: ACT.DEAL_FOLLOWUP_DATE_UPDATE_REQUESTED,
        payload: {
          id,
          followUpDate: dealFollowupDate,
          awardedCompanyId
        }
      })
    }

    if (proposalsFollowupDate) {
      yield put({
        type: ACT.DEAL_UPDATE_PROPOSALS_FOLLOWUP_REQUESTED,
        payload: {
          id,
          followUpDate: proposalsFollowupDate
        }
      })
    }

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Opportunity updated successfully',
        level: 'success'
      }
    })

    yield put({
      type: ACT.DEALS_BOARD_FETCH_DEALS_HEADER,
      payload: {
        id: userId,
        startDate,
        endDate,
        categories,
        contractTypes,
        awarded,
        geographicalAreas,
        priorities,
        scopes,
        sizes,
        sources,
        workTypes,
        teams
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue updating Opportunity',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* fetchDealCompanies(action) {
  try {
    const { dealId } = action.payload
    const url = `/v1/deals/${dealId}/companies`
    const response = yield makeGetRequest(url)
    yield put({
      type: ACT.DEAL_COMPANIES_FETCH_SUCCESS,
      payload: {
        dealCompanies: fromCompanies(response)
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue fetching the Opportunity companies',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* fetchDealTasks(action) {
  try {
    const { id, name } = action.payload
    const url = `/v1/deals/${id}/tasks`
    const response = yield makeGetRequest(url)

    yield put({
      type: ACT.DEAL_TASKS_FETCH_SUCCESS,
      payload: {
        tasks: response,
        dealName: name
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue fetching the Opportunity tasks',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* markTaskCompleted(action) {
  try {
    const { id } = action.payload
    const url = `/v1/tasks/${id}/markcompleted`
    const task = yield makePutRequest(url)

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Task was marked as completed!',
        level: 'success'
      }
    })
    yield put({
      type: ACT.DEAL_TASK_MARK_COMPLETED_SUCCESS,
      payload: {
        task
      }
    })

    yield refreshNotifications(put)
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue marking the Task as completed',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* markTaskNotCompleted(action) {
  try {
    const { id } = action.payload
    const url = `/v1/tasks/${id}/marknotcompleted`
    const task = yield makePutRequest(url)

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Task was marked as not completed!',
        level: 'success'
      }
    })
    yield put({
      type: ACT.DEAL_TASK_MARK_COMPLETED_SUCCESS,
      payload: {
        task
      }
    })
    yield put({
      type: ACT.DEAL_TASK_MARK_NOT_COMPLETED_SUCCESS
    })

    yield refreshNotifications(put)
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue marking the Task as not completed',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* updateTask(action) {
  const { task } = action.payload
  try {
    const url = `/v1/tasks/${task.id}`
    const taskObj = toTask(task, {}, true)
    taskObj.dealId = task.dealId

    const response = yield makePutRequest(url, taskObj)

    yield put({
      type: ACT.UPDATE_DEAL_TASK_SUCCESS,
      payload: {
        task: response
      }
    })

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Task updated successfully',
        level: 'success'
      }
    })

    yield refreshNotifications(put)
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue updating Task',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* deleteTask(action) {
  const { task } = action.payload
  try {
    const url = `/v1/tasks/${task.id}`
    yield makeDeleteRequest(url)

    yield put({
      type: ACT.DELETE_DEAL_TASK_SUCCESS,
      payload: {
        task
      }
    })

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Task deleted successfully',
        level: 'success'
      }
    })

    yield refreshNotifications(put)
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue deleting Task',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* updateProposalTask(action) {
  const { task, proposalStatus } = action.payload
  try {
    const url = `/v1/proposals/task/${task.id}`
    const taskObj = toTask(task, {}, true)

    taskObj.proposalId = task.proposals[0]

    const proposal = yield makePutRequest(url, taskObj)
    task.id = String(task.id)

    yield put({
      type: ACT.UPDATE_DEAL_BOARD_PROPOSAL_TASK_SUCCESS,
      payload: {
        proposal,
        status: proposalStatus
      }
    })

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Task updated successfully',
        level: 'success'
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue updating Task',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* fetchProposalsBoardData(action) {
  const { dealId } = action.payload
  try {
    const url = `/v1/proposals/board/deal/${dealId}`
    const proposalsBoard = yield makeGetRequest(url)
    yield put({
      type: ACT.PROPOSALS_DEAL_BOARD_DATA_FETCH_SUCCESS,
      payload: {
        proposalsBoard: fromBoardItems(proposalsBoard)
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue fetching Proposals',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* updateProposalStatus(action) {
  const { id, status, oldStatus } = action.payload
  try {
    const url = `/v1/proposals/board/${id}/status/${status}/deal`
    const response = yield makePutRequest(url)

    yield put({
      type: ACT.DEAL_PROPOSAL_UPDATE_STATUS_SUCCESS,
      payload: {
        proposals: fromBoardItems(response),
        oldStatus,
        id,
        status
      }
    })

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Proposal updated successfully',
        level: 'success'
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue updating Proposal',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* createProposalTask(action) {
  const { proposalId, task, proposalStatus } = action.payload
  try {
    const url = '/v1/proposals/scheduleTask/deal'
    const response = yield makePostRequest(url, toTask(task, { proposalId }, true))
    response.id = String(response.id)

    yield put({
      type: ACT.CREATE_DEAL_PROPOSAL_TASK_SUCCESS,
      payload: {
        proposals: response,
        status: proposalStatus
      }
    })

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Task created successfully',
        level: 'success'
      }
    })

    yield refreshNotifications(put)
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue creating Task for Proposal',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* createTask(action) {
  const { dealId, task } = action.payload
  try {
    const url = '/v1/tasks'
    const taskObj = toTask(task)
    taskObj.dealId = dealId
    const response = yield makePostRequest(url, taskObj)

    yield put({
      type: ACT.CREATE_DEAL_TASK_SUCCESS,
      payload: {
        tasks: response
      }
    })
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        level: 'success',
        title: 'Task created successfully'
      }
    })
    yield refreshNotifications(put)
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue creating Task for Opportunity',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* fetchDealProposalsTemplates(action) {
  try {
    const { dealId, templateType } = action.payload
    let url = `/v1/proposaltemplates/deal/${dealId}`
    if (templateType === 'HTML') {
      url = `/v1/proposaltemplates/html/deal/${dealId}`
    }
    const templates = yield makeGetRequest(url)

    yield put({
      type: ACT.DEAL_PROPOSALS_TEMPLATES_FETCH_SUCCESS,
      payload: {
        templates
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue fetching the Opportunity Proposal Templates',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* generateProposals(action) {
  try {
    const {
      editedSelectedProposalTemplate,
      dealId,
      clientsProposals,
      baseBid = '',
      editedProposalTemplateBase64,
      templateType,
      pageType,
      createProposalCb
    } = action.payload
    const { id } = editedSelectedProposalTemplate
    const response = yield makePostRequest(`/v1/deals/${dealId}/generateproposals/${id}`, {
      baseBid,
      proposals: clientsProposals,
      template: editedProposalTemplateBase64,
      ...(templateType === 'HTML' ? { pageType: pageType.toUpperCase() } : {})
    })

    yield put({
      type: ACT.DEAL_GENERATE_PROPOSALS_SUCCESS,
      payload: {
        proposal: response
      }
    })

    // this is done to refresh the list view for deal proposals tab
    createProposalCb?.()

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Proposals generated successfully',
        level: 'success'
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue generating Proposals',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* saveProposalDraft(action) {
  try {
    const {
      selectedTemplate,
      selectedGlobalTemplate,
      editedProposalTemplate,
      dealId,
      templateType,
      pageType
    } = action.payload

    const { id, title } = selectedTemplate
    let proposalTemplateId = id
    let proposalTemplateTitle = title
    const assignProposalToDealUrl = `/v1/proposaltemplates/${id}/deal/${dealId}`

    let template = ''
    if (selectedGlobalTemplate) {
      template = yield makePostRequest(
        assignProposalToDealUrl,
        templateType === 'HTML' ? { pageType: pageType.toUpperCase() } : {}
      )
      proposalTemplateId = template.id
      proposalTemplateTitle = template.title
    }

    let updateProposalTemplateUrl = `/v1/proposaltemplates/${proposalTemplateId}`
    if (templateType === 'HTML') {
      updateProposalTemplateUrl = `/v1/proposaltemplates/html/${proposalTemplateId}`
    }

    if (editedProposalTemplate) {
      template = yield makePutRequest(updateProposalTemplateUrl, {
        title: proposalTemplateTitle,
        content: editedProposalTemplate,
        ...(templateType === 'HTML' ? { pageType: pageType.toUpperCase() } : {})
      })
    }

    yield put({
      type: ACT.DEAL_PROPOSALS_ADD_EDITED_SELECTED_TEMPLATE_TO_STORE,
      payload: {
        editedSelectedProposalTemplate: template
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue saving Proposal Draft',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* addInternalTeam(action) {
  const { userRole, dealId } = action.payload
  try {
    const url = `/v1/deals/${dealId}/internalteam/${userRole.user.value}/${userRole.role.value}`
    const response = yield makePostRequest(url)

    yield put({
      type: ACT.DEAL_FETCH_SUCCESS,
      payload: {
        deal: augumentDealObject(cloneDeep(response))
      }
    })

    yield put({
      type: ACT.DEAL_ADD_INTERNAL_TEAM_SUCCESS
    })

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Opportunity updated successfully',
        level: 'success'
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue updating Opportunity',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* removeInternalTeam(action) {
  const { id, dealId } = action.payload
  try {
    const url = `/v1/deals/${dealId}/internalteam/${id}`
    const response = yield makeDeleteRequest(url)

    yield put({
      type: ACT.DEAL_FETCH_SUCCESS,
      payload: {
        deal: augumentDealObject(cloneDeep(response))
      }
    })

    yield put({
      type: ACT.DEAL_REMOVE_INTERNAL_TEAM_SUCCESS
    })

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Opportunity updated successfully',
        level: 'success'
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue updating Opportunity',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* addDealNote(action) {
  const { data } = action.payload
  try {
    const url = '/v1/notes'
    const note = yield makePostRequest(url, data)

    yield put({
      type: ACT.DEAL_ADD_NOTE_SUCCESS,
      payload: {
        note
      }
    })

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Note added successfully',
        level: 'success'
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue adding Note',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* updateDealNote(action) {
  const { data, noteId } = action.payload
  try {
    const url = `/v1/notes/${noteId}`
    const note = yield makePutRequest(url, data)

    yield put({
      type: ACT.DEAL_UPDATE_NOTE_SUCCESS,
      payload: {
        note,
        noteId
      }
    })

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Note updated successfully',
        level: 'success'
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue updating Note',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* fetchDealNotes(action) {
  try {
    const { id } = action.payload
    const url = `/v1/notes/deal/${id}`
    const notes = yield makeGetRequest(url)

    yield put({
      type: ACT.DEAL_NOTES_FETCH_SUCCESS,
      payload: {
        notes: notes
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue fetching Opportunity Notes',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* sendProposalsForReview(action) {
  const { proposalIds, users, dealId } = action.payload
  try {
    const url = '/v1/proposals/sendForReview'
    const proposals = yield makePutRequest(url, {
      proposalIds,
      userIds: users.map(u => u.value),
      splitMode: Object.keys(EMAIL_SPLIT_MODE)[0]
    })

    yield put({
      type: ACT.PROPOSALS_SEND_FOR_REVIEW_SUCCESS,
      payload: {
        proposals
      }
    })

    yield put({
      type: ACT.PROPOSALS_DEAL_BOARD_DATA_FETCH_REQUESTED,
      payload: {
        dealId
      }
    })

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Proposals successfully send for review',
        level: 'success'
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue sending Proposals for review',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* approveProposals(action) {
  const { proposalIds, dealId } = action.payload
  try {
    const url = '/v1/proposals/approve'
    const proposals = yield makePutRequest(url, {
      ids: proposalIds
    })

    yield put({
      type: ACT.APPROVE_PROPOSALS_SUCCESS,
      payload: {
        proposals
      }
    })

    yield put({
      type: ACT.PROPOSALS_DEAL_BOARD_DATA_FETCH_REQUESTED,
      payload: {
        dealId
      }
    })

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Proposals successfully approved',
        level: 'success'
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue approving Proposals',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* updateDealProposalsFollowUp(action) {
  const { id, followUpDate } = action.payload
  const date = utcDate(followUpDate)

  try {
    const url = `/v1/deals/${id}/followUpDate/${date}`
    yield makePutRequest(url)

    yield put({
      type: ACT.DEAL_FETCH_REQUESTED,
      payload: {
        id
      }
    })

    yield put({
      type: ACT.DEAL_UPDATE_PROPOSALS_FOLLOWUP_SUCCESS
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue updating Opportunity Proposals follow-up date',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* fetchDealSummaryInfo(action) {
  try {
    const { id } = action.payload
    const url = `/v1/deals/summary-info/${id}`
    const response = yield makeGetRequest(url)
    yield put({
      type: ACT.DEAL_SUMMARY_INFO_FETCH_SUCCESS,
      payload: {
        summaryInfo: response
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue fetching the Opportunity info',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* fetchDealAnalysis(action) {
  try {
    const { dealId } = action.payload
    const url = `/v1/deals/margin-analysis/${dealId}`
    const response = yield makeGetRequest(url)
    yield put({
      type: ACT.FETCH_DEAL_ANALYSIS_SUCCESS,
      payload: {
        analysis: response
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue fetching Deal Analysis',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* fetchDealDocumentsCount(action) {
  try {
    const { dealId } = action.payload
    const url = `/v1/deals/${dealId}/document-counts`
    const response = yield makeGetRequest(url)
    yield put({
      type: ACT.FETCH_DEAL_DOCUMENTS_COUNT_SUCCESS,
      payload: {
        count: response
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue fetching Documents count',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* deleteDealNote(action) {
  const { noteId } = action.payload
  try {
    const url = `/v1/notes/${noteId}`
    yield makeDeleteRequest(url)

    yield put({
      type: ACT.DEAL_DELETE_NOTE_SUCCESS,
      payload: {
        noteId
      }
    })

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Note deleted successfully',
        level: 'success'
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue deleting Note',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

const dealSagas = [
  takeLatest(ACT.DEAL_FETCH_REQUESTED, fetchDeal),
  takeLatest(ACT.DEAL_UPDATE_REQUESTED, updateDeal),
  takeLatest(ACT.DEAL_UPDATE_STATUS_REQUESTED, updateDealStatus),
  takeLatest(ACT.DEAL_BOARD_CONTRACTOR_AWARDED_UPDATE_REQUESTED, updateDealContractorAwarded),
  takeLatest(ACT.DEAL_COMPANIES_FETCH_REQUESTED, fetchDealCompanies),
  takeLatest(ACT.CREATE_DEAL_TASK_REQUESTED, createTask),
  takeLatest(ACT.DEAL_TASKS_FETCH_REQUESTED, fetchDealTasks),
  takeLatest(ACT.DEAL_TASK_MARK_COMPLETED_REQUESTED, markTaskCompleted),
  takeLatest(ACT.DEAL_TASK_MARK_NOT_COMPLETED_REQUESTED, markTaskNotCompleted),
  takeLatest(ACT.UPDATE_DEAL_TASK_REQUESTED, updateTask),
  takeLatest(ACT.DELETE_DEAL_TASK_REQUESTED, deleteTask),
  takeLatest(ACT.DEAL_PROPOSALS_TEMPLATES_FETCH_REQUESTED, fetchDealProposalsTemplates),
  takeLatest(ACT.PROPOSALS_DEAL_BOARD_DATA_FETCH_REQUESTED, fetchProposalsBoardData),
  takeLatest(ACT.DEAL_PROPOSAL_UPDATE_STATUS_REQUESTED, updateProposalStatus),
  takeLatest(ACT.CREATE_DEAL_PROPOSAL_TASK_REQUESTED, createProposalTask),
  takeLatest(ACT.UPDATE_DEAL_BOARD_PROPOSAL_TASK_REQUESTED, updateProposalTask),
  takeLatest(ACT.DEAL_GENERATE_PROPOSALS_REQUESTED, generateProposals),
  takeLatest(ACT.DEAL_SAVE_PROPOSAL_DRAFT, saveProposalDraft),
  takeLatest(ACT.DEAL_ADD_INTERNAL_TEAM_REQUESTED, addInternalTeam),
  takeLatest(ACT.DEAL_REMOVE_INTERNAL_TEAM_REQUESTED, removeInternalTeam),
  takeLatest(ACT.DEAL_ADD_NOTE_REQUESTED, addDealNote),
  takeLatest(ACT.DEAL_NOTES_FETCH_REQUESTED, fetchDealNotes),
  takeLatest(ACT.DEAL_UPDATE_NOTE_REQUESTED, updateDealNote),
  takeLatest(ACT.DEAL_DATE_UPDATE_REQUESTED, updateDealDate),
  takeLatest(ACT.PROPOSALS_SEND_FOR_REVIEW_REQUESTED, sendProposalsForReview),
  takeLatest(ACT.APPROVE_PROPOSALS_REQUESTED, approveProposals),
  takeLatest(ACT.DEAL_UPDATE_PROPOSALS_FOLLOWUP_REQUESTED, updateDealProposalsFollowUp),
  takeLatest(ACT.DEAL_DELETE_REQUESTED, deleteDeal),
  takeLatest(ACT.DEAL_SUMMARY_INFO_FETCH_REQUESTED, fetchDealSummaryInfo),
  takeLatest(ACT.FETCH_DEAL_ANALYSIS_REQUESTED, fetchDealAnalysis),
  takeLatest(ACT.DEAL_DELETE_NOTE_REQUESTED, deleteDealNote),
  takeLatest(ACT.FETCH_DEAL_DOCUMENTS_COUNT_REQUESTED, fetchDealDocumentsCount)
]

export default dealSagas
